<script setup>
import { computed, onMounted } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { useAIControl } from 'dashboard/composables/smarttec/useAIControl';

const {
  status,
  isLoading,
  error,
  automaticSchedule,
  manualActivation,
  effectiveActive,
  activationSource,
  setManualActivation,
  startMonitoring,
} = useAIControl();

const statusLabel = computed(() => {
  if (activationSource.value === 'automatic_schedule') {
    return 'Activa por horario automático';
  }

  if (activationSource.value === 'manual_activation') {
    return 'Activa manualmente';
  }

  return 'Inactiva';
});

const description = computed(() => {
  if (automaticSchedule.value) {
    return 'La IA permanece activa obligatoriamente entre las 21:55 y las 08:00.';
  }

  if (manualActivation.value) {
    return 'La IA está atendiendo porque fue activada manualmente.';
  }

  return 'La IA está apagada. Se encenderá automáticamente a las 21:55.';
});

const buttonLabel = computed(() =>
  manualActivation.value
    ? 'Desactivar IA manual'
    : 'Activar IA ahora'
);

const statusClass = computed(() =>
  effectiveActive.value
    ? 'bg-n-teal-3 text-n-teal-11'
    : 'bg-n-slate-3 text-n-slate-11'
);

const dotClass = computed(() =>
  effectiveActive.value
    ? 'bg-n-teal-9'
    : 'bg-n-slate-8'
);

const changedBy = computed(
  () => status.value?.changed_by || null
);

const changedAt = computed(() => {
  const value = status.value?.changed_at;

  if (!value) return null;

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleString('es-PE');
});

const toggleManual = async () => {
  try {
    const newValue = !manualActivation.value;

    await setManualActivation(newValue);

    if (automaticSchedule.value) {
      useAlert(
        newValue
          ? 'Activación manual guardada. El horario automático continúa activo.'
          : 'Activación manual apagada. La IA continúa activa por horario automático.'
      );
      return;
    }

    useAlert(
      newValue
        ? 'IA Smarttec activada manualmente.'
        : 'IA Smarttec desactivada manualmente.'
    );
  } catch {
    useAlert(
      'No se pudo cambiar el estado de la IA Smarttec.'
    );
  }
};

onMounted(startMonitoring);
</script>

<template>
  <section
    class="mb-6 rounded-xl border border-n-weak bg-n-solid-1 p-5"
  >
    <div
      class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
    >
      <div class="flex flex-col gap-2">
        <div class="flex flex-wrap items-center gap-2">
          <h3 class="text-heading-2 text-n-slate-12">
            Asistente IA Smarttec
          </h3>

          <span
            class="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-body-small font-medium"
            :class="statusClass"
          >
            <span
              class="size-2 rounded-full"
              :class="dotClass"
            />

            {{ statusLabel }}
          </span>
        </div>

        <p class="text-body-main text-n-slate-11">
          {{ description }}
        </p>

        <div
          class="flex flex-wrap gap-x-4 gap-y-1 text-body-small text-n-slate-10"
        >
          <span>
            Horario automático:
            <strong class="text-n-slate-11">
              21:55 → 08:00
            </strong>
          </span>

          <span>
            Zona horaria:
            <strong class="text-n-slate-11">
              America/Lima
            </strong>
          </span>
        </div>

        <div
          v-if="changedBy || changedAt"
          class="text-body-small text-n-slate-10"
        >
          <span v-if="changedBy">
            Último cambio: {{ changedBy }}
          </span>

          <span v-if="changedBy && changedAt"> · </span>

          <span v-if="changedAt">
            {{ changedAt }}
          </span>
        </div>

        <p
          v-if="automaticSchedule"
          class="text-body-small font-medium text-n-amber-11"
        >
          El control manual no puede apagar el horario automático.
        </p>

        <p
          v-if="error"
          class="text-body-small text-n-ruby-11"
        >
          No se pudo consultar el servicio IA.
        </p>
      </div>

      <div class="shrink-0">
        <Button
          :label="buttonLabel"
          size="sm"
          :is-loading="isLoading"
          @click="toggleManual"
        />
      </div>
    </div>
  </section>
</template>
